// A new species' rank, as GBIF writes it - the 'rank' field's choices
// (champis-species.forum.page.ranks.* for their labels).
export const RANKS = ['GENUS', 'SPECIES', 'SUBSPECIES', 'VARIETY', 'FORM'];

// Row field access is dynamic by design - TABLES drives which attribute
// accessor methods exist on which row for a given table (Flarum's own
// Model.attribute() convention: a `gbifId` field means a callable
// `row.gbifId()`, not a plain property), so there's no single keyed
// interface that covers every table's own row shape. This is the minimal
// typed escape hatch for that: an index signature instead of `any`, so a
// lookup still returns `unknown` (forcing the typeof-function guard in
// fieldValue()) rather than silently allowing anything.

// `gbifSearch` fields resolve a real GBIF id via /api/species-autocomplete
// (SpeciesGbifSearchField) - `fillField`, when present, is the key of a
// companion text field on this same table that gets auto-filled from the
// selected result's scientificName, so e.g. picking a species for
// "gbifId" also fills "scientificName" without retyping it (and without
// the risk of the two disagreeing). "champis-species-new" is the one
// exception: its own gbifId isn't in GBIF yet (that's the whole point of
// the table), so there's nothing to search - the server assigns a
// synthetic id on create instead (see NewSpecies::boot()) and the field
// is `readonly` here, display-only, not part of the add/edit form. Only
// its parentGbifId (a real, existing taxon) searches.
export const TABLES = [{
  type: 'champis-species-accepted',
  titleKey: 'champis-species.forum.page.accepted_title',
  hintKey: 'champis-species.forum.page.accepted_hint',
  icon: 'fas fa-check',
  sortField: 'scientificName',
  fields: [{
    key: 'gbifId',
    labelKey: 'champis-species.forum.page.field.gbif_id',
    kind: 'gbifSearch',
    fillField: 'scientificName'
  }, {
    key: 'scientificName',
    labelKey: 'champis-species.forum.page.field.scientific_name',
    kind: 'text'
  }]
}, {
  type: 'champis-species-removed',
  titleKey: 'champis-species.forum.page.removed_title',
  hintKey: 'champis-species.forum.page.removed_hint',
  icon: 'fas fa-ban',
  sortField: 'scientificName',
  fields: [{
    key: 'gbifId',
    labelKey: 'champis-species.forum.page.field.gbif_id',
    kind: 'gbifSearch',
    fillField: 'scientificName'
  }, {
    key: 'scientificName',
    labelKey: 'champis-species.forum.page.field.scientific_name',
    kind: 'text'
  }]
}, {
  type: 'champis-species-parent',
  titleKey: 'champis-species.forum.page.parent_title',
  hintKey: 'champis-species.forum.page.parent_hint',
  icon: 'fas fa-sitemap',
  sortField: 'scientificName',
  fields: [{
    key: 'gbifId',
    labelKey: 'champis-species.forum.page.field.gbif_id',
    kind: 'gbifSearch',
    fillField: 'scientificName'
  }, {
    key: 'parentGbifId',
    labelKey: 'champis-species.forum.page.field.parent_gbif_id',
    kind: 'gbifSearch',
    fillField: 'parentScientificName'
  }, {
    key: 'scientificName',
    labelKey: 'champis-species.forum.page.field.scientific_name',
    kind: 'text'
  }, {
    key: 'parentScientificName',
    labelKey: 'champis-species.forum.page.field.parent_scientific_name',
    kind: 'text'
  }]
}, {
  type: 'champis-species-new',
  titleKey: 'champis-species.forum.page.new_title',
  hintKey: 'champis-species.forum.page.new_hint',
  icon: 'fas fa-plus-circle',
  sortField: 'canonicalName',
  fields: [
  // Server-assigned on create (see NewSpeciesResource/NewSpecies) - a
  // genuinely new species has no real GBIF id to search or type in,
  // so this is display-only: shown in the list but never part of the
  // add/edit form.
  {
    key: 'gbifId',
    labelKey: 'champis-species.forum.page.field.gbif_id',
    kind: 'readonly'
  }, {
    key: 'parentGbifId',
    labelKey: 'champis-species.forum.page.field.parent_gbif_id',
    kind: 'gbifSearch',
    fillField: 'parentScientificName'
  }, {
    key: 'parentScientificName',
    labelKey: 'champis-species.forum.page.field.parent_scientific_name',
    kind: 'text'
  }, {
    key: 'canonicalName',
    labelKey: 'champis-species.forum.page.field.canonical_name',
    kind: 'text'
  }, {
    key: 'author',
    labelKey: 'champis-species.forum.page.field.author',
    kind: 'text'
  }, {
    key: 'rank',
    labelKey: 'champis-species.forum.page.field.rank',
    kind: 'rank'
  }]
}];

// Not a tab of its own - a synonym only ever makes sense attached to one
// Accepted row, so each Accepted row lists (and adds to) its own synonyms
// directly (see SpeciesCorrectionsPage). Still a real resource with its own
// endpoints (SynonymSpeciesResource), just not a TABLES entry.
export const SYNONYM_TYPE = 'champis-species-synonym';

/**
 * A row's `key` attribute. .call(row), not value() - Model.attribute()'s
 * getters read this.attribute(), so a detached call has no `this` and
 * throws mid-render.
 */
export function fieldValue(row, key) {
  const value = row[key];
  return typeof value === 'function' ? String(value.call(row) ?? '') : '';
}
flarum.reg.add('champis-species', 'forum/utils/speciesCorrectionTables', { RANKS: RANKS,TABLES: TABLES,SYNONYM_TYPE: SYNONYM_TYPE,fieldValue: fieldValue, });